/**
 * Crypto utilities for generating secrets from setup data
 * Matches the backend implementation exactly
 * Uses native Web Crypto API for SHA-256 hashing
 */

/**
 * Generates a secret from an object using SHA-256
 * This MUST match the backend implementation exactly:
 * - Same salt value
 * - Same key sorting
 * - Same JSON stringification
 * - Same SHA-256 algorithm
 * 
 * @param {Object} formData - The signup form data
 * @returns {Promise<string>} - A SHA-256 hash (hex string)
 */
export const generateSecretFromObject = async (formData) => {
    // Create a copy of the object and add salt (matching backend implementation)
    const testObject = { ...formData };
    testObject.__salt = ['s', 'a', 'l', 't'].join('');
    
    // Sort the keys of the object (matching backend implementation)
    const sortedKeys = Object.keys(testObject).sort();

    // Create a new object with sorted keys
    const sortedObject = {};
    sortedKeys.forEach(key => {
        sortedObject[key] = testObject[key];
    });

    // Convert the sorted object to a JSON string
    const jsonString = JSON.stringify(sortedObject);

    // Use the native Web Crypto API to produce a SHA-256 hash value
    // Return the hash as the secret (matching backend implementation)
    const encoder = new TextEncoder();
    const data = encoder.encode(jsonString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
};
